'use client';
import { Suspense, lazy } from 'react';
import type { HostTimeSeriesChartProps } from './timeSeriesChartTypes';
import { EmptyState } from './states';

/** The real chart, where `Sparkline` is the shape.
 *
 *  Sparkline draws a figure you read the number beside; this one answers "which day, and how much" —
 *  so it carries ticks, a cursor tooltip and a legend, and it accepts two series in DIFFERENT units on
 *  their own axes. Tokens run to millions and cost to single dollars: normalising each to its own peak,
 *  which is what the hand-rolled versions did, makes two bars the same height mean nothing.
 *
 *  Recharts arrives through a lazy import on purpose. It measured ~376 KB uncompressed, and this
 *  component is published to plugin bundles through the UI runtime — a static import would drag the
 *  library into the chunk that Account and Settings already pull in for everything else. */

const Impl = lazy(() => import('./TimeSeriesChartImpl').then((module) => ({ default: module.TimeSeriesChartImpl })));

export function TimeSeriesChart(props: HostTimeSeriesChartProps) {
  const { data, height = 220, emptyText, timeAxis } = props;
  if (timeAxis) {
    const [from, to] = timeAxis.domain;
    if (!Number.isFinite(from) || !Number.isFinite(to) || from >= to) throw new Error('Invalid time-axis domain');
    let previous = -Infinity;
    for (const point of data) {
      const timestamp = point[timeAxis.dataKey];
      if (typeof timestamp !== 'number' || !Number.isFinite(timestamp) || timestamp <= previous) {
        throw new Error('Time-axis points require unique ascending finite timestamps');
      }
      previous = timestamp;
    }
  }
  // Resolved before the lazy boundary: an empty range should not fetch a charting library to say so.
  if (data.length === 0) {
    return <EmptyState title={emptyText ?? ''} />;
  }
  // The placeholder takes the same space the chart will, so the figure does not jump when Recharts arrives.
  const placeholder = height === 'fill'
    ? <div className="min-h-60 flex-1 animate-pulse rounded-lg bg-muted/40" aria-hidden />
    : <div className="animate-pulse rounded-lg bg-muted/40" style={{ height }} aria-hidden />;
  return (
    <Suspense fallback={placeholder}>
      <Impl {...props} />
    </Suspense>
  );
}
